import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from 'react'
import {IntlProvider} from 'react-intl'

import {logAndCaptureErrorMessage} from '../../../utils/logging/errorHandlers'
import {
  getFirstDayOfWeek,
  loadCalendarStrings
} from '../../services/dateLocalization'
import {getLocaleByCode} from '../../services/locale'
import {
  localeLanguageMapper,
  mapArabicToLatinNumberSystem
} from '../../services/mappingLocales'
import {getTranslationErrorLogger, LogPayload} from '../../utils'

type LocalizationStrings = Record<string, unknown>

export interface CalendarStrings {
  monthNames: string[]
  weekDayNamesShort: string[]
}

interface CalendarConfig extends CalendarStrings {
  firstDayOfWeek: number
}

export interface ConfigureI18nProps {
  /** The base URL where the application's static files (including the i18n assets) are hosted */
  publicUrl?: string
  /** The i18n assets will be configured for this languageCode */
  languageCode: string
  /** The user country code */
  countryCode: string
  /** The tree of components that will have access to the i18n context */
  children: ReactNode
  /** Fallback component to be rendered while loading the i18n assets */
  FallbackComponent?: React.ComponentType
  /** Custom translation error handler, can be used as logger for translation errors */
  translationErrorHandler?: (logPayload: LogPayload) => void
  /** Preloaded messages */
  preloadedMessages?: LocalizationStrings | null
  /** Preloaded calendar strings */
  preloadedCalendarStrings?: CalendarStrings | null
}

const defaultCalendarConfig: CalendarConfig = {
  firstDayOfWeek: 1,
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ],
  weekDayNamesShort: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
}

/**
 * Custom context that makes the i18n calendar assets available. Translation
 * messages flow through `IntlProvider`, not through this context.
 */
export const ConfigureI18nContext = createContext<CalendarConfig>(
  defaultCalendarConfig
)

const DefaultFallbackComponent = () => null

const fetchWithRetry = async (
  url: string,
  retries = 2,
  delay = 200
): Promise<Response> => {
  let lastStatus: number | undefined
  try {
    const res = await fetch(url)
    if (res.ok) return res
    lastStatus = res.status
  } catch (e) {
    if (retries === 0) throw e
  }

  if (retries === 0) {
    throw new Error(
      `Failed to fetch translations${lastStatus ? ` (status: ${lastStatus})` : ''}`
    )
  }
  await new Promise(resolve => setTimeout(resolve, delay))
  return fetchWithRetry(url, retries - 1, delay * 2)
}

/**
 * Load i18n assets and provide localization context.
 * @param props
 * @param props.publicUrl - The base URL where the application's static files (including the i18n assets) are hosted.
 * @param props.languageCode - The user's language code.
 * @param props.countryCode - The user's country code.
 * @param props.children - Children to be rendered once i18n is ready
 * @param props.FallbackComponent - Fallback component to be rendered while loading the i18n assets.
 * @param props.translationErrorHandler - Custom error handler, can be used as logger for translation errors.
 * @param props.preloadedMessages - Preloaded messages. Allow providing messages upfront and, in that case, skip loading them client side.
 * @param props.preloadedCalendarStrings - Preloaded calendar strings, skips the client fetch when provided.
 */
const ConfigureI18n = ({
  publicUrl = '/',
  languageCode,
  countryCode,
  children,
  FallbackComponent = DefaultFallbackComponent,
  translationErrorHandler,
  preloadedMessages = null,
  preloadedCalendarStrings = null
}: ConfigureI18nProps) => {
  const [messages, setMessages] = useState<{
    languageCode: string
    values: LocalizationStrings
  } | null>(
    preloadedMessages ? {languageCode, values: preloadedMessages} : null
  )

  const [calendarStrings, setCalendarStrings] = useState<{
    languageCode: string
    values: CalendarStrings
  } | null>(
    preloadedCalendarStrings
      ? {languageCode, values: preloadedCalendarStrings}
      : null
  )

  const mappedLanguage = localeLanguageMapper(languageCode)
  const mappedNumberSystemLocale = mapArabicToLatinNumberSystem(mappedLanguage)

  const locale = getLocaleByCode(languageCode)

  // Load messages
  useEffect(() => {
    if (messages?.languageCode === languageCode) return

    fetchWithRetry(`${publicUrl}translations/${locale}.json`)
      .then(res => res.json())
      .then(response => {
        setMessages({
          languageCode,
          values: response as unknown as LocalizationStrings
        })
      })
      .catch((error: Error) => {
        logAndCaptureErrorMessage(
          `An error occurred while loading i18n assets - ${error.message}`,
          {level: 'error', tags: {mappedLanguage}}
        )
        setMessages({languageCode, values: {}})
      })
  }, [languageCode, locale, mappedLanguage, messages, publicUrl])

  // Load calendar strings
  useEffect(() => {
    if (!countryCode) return
    if (calendarStrings?.languageCode === languageCode) return

    loadCalendarStrings(languageCode, publicUrl)
      .then(values => {
        setCalendarStrings({languageCode, values})
      })
      .catch(() => {
        setCalendarStrings({languageCode, values: defaultCalendarConfig})
      })
  }, [mappedLanguage, countryCode, languageCode, calendarStrings, publicUrl])

  const calendarConfig = useMemo(
    () => ({
      ...(calendarStrings?.values ?? defaultCalendarConfig),
      firstDayOfWeek: getFirstDayOfWeek(countryCode)
    }),
    [countryCode, calendarStrings?.values]
  )

  if (messages) {
    return (
      <IntlProvider
        textComponent="span"
        locale={mappedNumberSystemLocale}
        messages={messages.values as Record<string, string>}
        onError={
          translationErrorHandler
            ? getTranslationErrorLogger(mappedLanguage, translationErrorHandler)
            : undefined
        }
      >
        <ConfigureI18nContext.Provider value={calendarConfig}>
          {children}
        </ConfigureI18nContext.Provider>
      </IntlProvider>
    )
  }

  return <FallbackComponent />
}

export default ConfigureI18n
